import { useEffect, useRef, useState } from "react";
import { Send, MessageCircle, ChevronLeft } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const fmtTime = (iso) =>
  new Date(iso).toLocaleString("fr-FR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });

function Thread({ msgs, ownRole }) {
  const endRef = useRef(null);
  useEffect(() => {
    endRef.current?.scrollIntoView({ block: "end" });
  }, [msgs.length]);

  if (msgs.length === 0) {
    return <p className="text-sm text-[#78716C] italic text-center py-8">Aucun message pour l'instant. Écrivez-nous !</p>;
  }
  return (
    <div className="space-y-2.5 max-h-72 overflow-y-auto no-scrollbar pr-1" data-testid="message-thread" data-lenis-prevent>
      {msgs.map((m) => {
        const mine = m.sender_role === ownRole;
        return (
          <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid="message-bubble">
            <div
              className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm leading-snug ${
                mine
                  ? "bg-[#6B1226] text-[#FAF6F0] rounded-br-md"
                  : "bg-white border border-[#6B1226]/15 text-[#1C1917] rounded-bl-md"
              }`}
            >
              <p>{m.text}</p>
              <p className={`text-[0.6rem] mt-1 ${mine ? "text-[#FAF6F0]/60" : "text-[#A8A29E]"}`}>
                {mine ? "Vous" : "Le sommelier"} · {fmtTime(m.created_at)}
              </p>
            </div>
          </div>
        );
      })}
      <div ref={endRef} />
    </div>
  );
}

function Composer({ onSend, busy, text, setText, testIdPrefix }) {
  return (
    <div className="mt-3 flex gap-2">
      <input
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && onSend()}
        placeholder="Votre message…"
        className="flex-1 bg-white border border-[#6B1226]/15 rounded-full px-4 py-3 text-base outline-none focus:border-[#6B1226]/50 transition-colors"
        data-testid={`${testIdPrefix}-input`}
      />
      <button
        onClick={onSend}
        disabled={busy || !text.trim()}
        className="shrink-0 w-12 h-12 rounded-full bg-[#6B1226] text-[#FAF6F0] flex items-center justify-center hover:bg-[#580E1D] transition-colors disabled:opacity-50"
        data-testid={`${testIdPrefix}-send`}
        aria-label="Envoyer"
      >
        <Send size={17} />
      </button>
    </div>
  );
}

export function ClientMessenger() {
  const [msgs, setMsgs] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/messages/mine").then((res) => setMsgs(res.data)).catch(() => setMsgs([]));

  useEffect(() => {
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, []);

  const send = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      await api.post("/messages", { text });
      setText("");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white rounded-3xl border border-[#6B1226]/10 card-shadow p-6" data-testid="client-messenger">
      <h2 className="font-display text-2xl font-semibold text-[#1C1917] flex items-center gap-2.5 mb-4">
        <MessageCircle size={20} className="text-[#6B1226]" /> Écrire au sommelier
      </h2>
      {msgs === null ? <p className="text-sm text-[#57534E] italic">Chargement…</p> : <Thread msgs={msgs} ownRole="client" />}
      <Composer onSend={send} busy={busy} text={text} setText={setText} testIdPrefix="client-message" />
    </div>
  );
}

export function AdminMessenger() {
  const [convos, setConvos] = useState(null);
  const [active, setActive] = useState(null);
  const [thread, setThread] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const loadConvos = () => api.get("/admin/messages").then((res) => setConvos(res.data)).catch(() => setConvos([]));
  const loadThread = (uid) => api.get(`/admin/messages/${uid}`).then((res) => setThread(res.data)).catch(() => {});

  useEffect(() => {
    loadConvos();
    const t = setInterval(loadConvos, 10000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!active) return;
    loadThread(active);
    const t = setInterval(() => loadThread(active), 5000);
    return () => clearInterval(t);
  }, [active]);

  const send = async () => {
    if (!text.trim() || !active) return;
    setBusy(true);
    try {
      await api.post(`/admin/messages/${active}`, { text });
      setText("");
      loadThread(active);
      loadConvos();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (active) {
    const convo = convos?.find((c) => c.user_id === active);
    return (
      <div className="mt-8 bg-white rounded-2xl border border-[#6B1226]/10 p-5" data-testid="admin-messenger-thread">
        <button
          onClick={() => {
            setActive(null);
            loadConvos();
          }}
          className="inline-flex items-center gap-1 text-xs font-semibold text-[#6B1226] hover:underline mb-4"
          data-testid="admin-messages-back"
        >
          <ChevronLeft size={14} /> Toutes les conversations
        </button>
        <h3 className="font-display text-xl font-semibold text-[#1C1917] mb-4">
          {convo?.customer_name || "Client"}
          {convo?.customer_email && <span className="block text-xs font-normal text-[#78716C] mt-0.5">{convo.customer_email}</span>}
        </h3>
        <Thread msgs={thread} ownRole="admin" />
        <Composer onSend={send} busy={busy} text={text} setText={setText} testIdPrefix="admin-reply" />
      </div>
    );
  }

  return (
    <div className="mt-8" data-testid="admin-messenger">
      {convos === null ? (
        <p className="text-sm text-[#57534E] italic">Chargement…</p>
      ) : convos.length === 0 ? (
        <div className="bg-white rounded-2xl border border-[#6B1226]/10 p-10 text-center" data-testid="admin-messages-empty">
          <MessageCircle size={36} className="text-[#C5A059] mx-auto" />
          <p className="font-display text-xl text-[#1C1917] mt-3">Aucune conversation</p>
          <p className="text-sm text-[#57534E] mt-1.5">Les messages des clients apparaîtront ici.</p>
        </div>
      ) : (
        <ul className="space-y-3" data-testid="admin-conversations-list">
          {convos.map((c) => (
            <li key={c.user_id}>
              <button
                onClick={() => setActive(c.user_id)}
                className="w-full text-left bg-white rounded-2xl border border-[#6B1226]/10 p-4 flex items-center gap-3 hover:border-[#6B1226]/40 transition-colors"
                data-testid="admin-convo-item"
              >
                <span className="w-11 h-11 rounded-full bg-[#6B1226] text-[#FAF6F0] flex items-center justify-center font-display text-lg font-semibold shrink-0">
                  {(c.customer_name || "C").charAt(0).toUpperCase()}
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block font-semibold text-[#1C1917] text-sm">{c.customer_name}</span>
                  <span className="block text-xs text-[#78716C] truncate mt-0.5">{c.last_message?.text}</span>
                </span>
                <span className="flex flex-col items-end gap-1 shrink-0">
                  <span className="text-[0.6rem] text-[#A8A29E]">{fmtTime(c.last_message?.created_at)}</span>
                  {c.unread > 0 && (
                    <span
                      className="rounded-full bg-[#6B1226] text-[#FAF6F0] text-[0.65rem] font-bold min-w-[1.4rem] h-[1.4rem] flex items-center justify-center px-1.5"
                      data-testid="admin-convo-unread"
                    >
                      {c.unread}
                    </span>
                  )}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
